import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import SocialActions from '../../components/SocialActions/SocialActions';
import ContentEditorModal from '../../components/ContentEditorModal/ContentEditorModal';
import { useAuth } from '../../contexts/AuthContext';

export default function Seatat() {
  const { user } = useAuth();
  const [hymns, setHymns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [isNewContent, setIsNewContent] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const apiURL = process.env.REACT_APP_API_URL || '/api';

  const canEditContent = Boolean(user?.canEdit || user?.canUpdate || user?.isAdmin);
  const canInsertContent = Boolean(user?.canInsert || user?.isAdmin);

  const loadHymns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiURL}/content?type=seatat`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          const mapped = json.data.map(item => ({
            id: item.itemId !== undefined ? item.itemId : item._id,
            _id: item._id,
            title1: item.title,
            content: Array.isArray(item.content) ? item.content : (item.content?.sections || []),
            raw: item
          }));
          setHymns(mapped);
          if (mapped.length > 0 && !selectedId) {
            setSelectedId(mapped[0].id);
          }
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('API error for Seatat:', err.message);
    }
    setHymns([]);
    setLoading(false);
  }, [apiURL, selectedId]);

  useEffect(() => {
    loadHymns();
  }, [loadHymns]);

  // Click outside listener for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredHymns = useMemo(() => {
    if (!searchTerm.trim()) return hymns;
    const term = searchTerm.toLowerCase();
    return hymns.filter((h) => {
      const titleMatch = h.title1?.toLowerCase().includes(term);
      const contentMatch = h.content?.some((section) =>
        section.contains?.some(
          (c) =>
            c.title?.toLowerCase().includes(term) ||
            c.main?.toLowerCase().includes(term)
        )
      );
      return titleMatch || contentMatch;
    });
  }, [searchTerm, hymns]);

  const selectedHymn = hymns.find((h) => h.id === selectedId) || hymns[0];

  const highlightText = (text) => {
    if (!text) return '';
    // Unescape literal \n into real newlines
    const cleanText = typeof text === 'string' ? text.replace(/\\n/g, '\n') : text;
    if (!searchTerm.trim()) return cleanText;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi");
    return cleanText.split(regex).map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark key={i} className="search-highlight bg-amber-200 text-amber-900 rounded px-0.5">{part}</mark>
      ) : part
    );
  };

  const handleOpenEdit = (hymn) => {
    setEditingItem({
      _id: hymn._id,
      contentType: 'seatat',
      title: hymn.title1,
      itemId: hymn.id,
      content: hymn.content
    });
    setIsNewContent(false);
    setEditorOpen(true);
  };

  const handleOpenInsert = () => {
    const nextId = hymns.length > 0 ? Math.max(...hymns.map(h => typeof h.id === 'number' ? h.id : 0)) + 1 : 1;
    setEditingItem({
      contentType: 'seatat',
      title: '',
      itemId: nextId,
      content: [
        {
          sub_title: 'መግቢያ',
          contains: [
            { title: 'ጸሎት', main: '' }
          ]
        }
      ]
    });
    setIsNewContent(true);
    setEditorOpen(true);
  };

  const handleEditorSaved = () => {
    loadHymns();
  };

  return (
    <div className="page-container seatat-theme max-w-7xl mx-auto p-4 sm:p-6">
      {/* Page Header */}
      <div className="page-header text-center mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="text-left">
            <h1 className="page-main-title text-3xl font-extrabold text-gray-900 flex items-center gap-2">
              <span>⏰</span> መጽሐፈ ሰዓታት
            </h1>
            <div className="page-subtitle text-gray-600 text-sm mt-1">Book of Hours - ሰዓታዊ ጸሎቶች</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">
                ⚡ ከመረጃ ቋት (MongoDB) የተገኘ
              </span>
              {canEditContent && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium flex items-center gap-1">
                  <span>👤</span> የተፈቀደ አርታዒ ({user?.email || 'admin'})
                </span>
              )}
            </div>
          </div>
          {canInsertContent && (
            <button
              onClick={handleOpenInsert}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold px-4 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-2 self-start sm:self-auto cursor-pointer text-sm"
            >
              <span>➕</span>
              <span>አዲስ ሰዓታት አክል</span>
            </button>
          )}
        </div>
      </div>

      {/* Modern Controls & Select Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Modern Styled Select Box */}
          <div className="relative" ref={dropdownRef}>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <span>⏰</span> የሰዓታት ክፍል ይምረጡ (Select Canonical Hour)
              </span>
              <span className="text-[11px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                {hymns.length} ክፍሎች
              </span>
            </label>

            {/* Custom Modern Dropdown Button */}
            <button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-50 to-white hover:from-blue-50/50 hover:to-white border-2 border-gray-200 hover:border-blue-400 focus:border-blue-500 rounded-xl shadow-sm text-left transition-all duration-200 outline-none"
            >
              <div className="flex items-center gap-3 overflow-hidden">
                <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                  {selectedHymn?.id || '፩'}
                </span>
                <div className="truncate">
                  <span className="font-bold text-sm text-gray-800 block truncate">
                    {selectedHymn?.title1 || 'ሰዓታት ይምረጡ...'}
                  </span>
                  <span className="text-[11px] text-gray-500 block truncate">
                    {selectedHymn?.content?.length || 0} ንዑሳን ክፍሎች
                  </span>
                </div>
              </div>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 flex-shrink-0 ${dropdownOpen ? 'rotate-180 text-blue-600' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Modern Dropdown Popover */}
            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 z-30 max-h-80 overflow-hidden flex flex-col animate-fadeIn">
                <div className="p-3 border-b border-gray-100 bg-gray-50/70 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-600">
                    የሰዓታት ዝርዝር ({filteredHymns.length})
                  </span>
                  <button
                    onClick={() => setDropdownOpen(false)}
                    className="text-gray-400 hover:text-gray-600 text-xs px-2 py-0.5 rounded-md hover:bg-gray-200"
                  >
                    ✕ ዝጋ
                  </button>
                </div>
                <div className="overflow-y-auto p-2 space-y-1">
                  {loading ? (
                    <div className="p-4 text-center text-xs text-gray-500">በመጫን ላይ...</div>
                  ) : filteredHymns.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-500">ምንም ክፍል አልተገኘም</div>
                  ) : (
                    filteredHymns.map((hymn) => {
                      const isSelected = hymn.id === selectedId;
                      return (
                        <button
                          key={hymn.id}
                          type="button"
                          onClick={() => {
                            setSelectedId(hymn.id);
                            setDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left transition-all text-xs sm:text-sm ${
                            isSelected
                              ? 'bg-blue-600 text-white font-bold shadow-sm'
                              : 'hover:bg-blue-50 text-gray-700 hover:text-blue-700'
                          }`}
                        >
                          <div className="flex items-center gap-2.5 truncate">
                            <span className={`w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                              isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {hymn.id}
                            </span>
                            <span className="truncate">{hymn.title1}</span>
                          </div>
                          {isSelected && (
                            <span className="text-white text-xs font-bold flex-shrink-0">✓</span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Search Box */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 flex items-center justify-between">
              <span>🔍 በይዘት ውስጥ ይፈልጉ (Search Content)</span>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-[11px] text-red-500 hover:underline"
                >
                  ፍለጋ አጽዳ ✕
                </button>
              )}
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="ለምሳሌ፡- ሰዓተ ጸሎት፣ ድንግል ማርያም፣ መዝሙር..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 text-sm border-2 border-gray-200 focus:border-blue-500 rounded-xl outline-none transition shadow-sm"
              />
              <span className="absolute left-3.5 top-3.5 text-gray-400 text-sm">🔍</span>
            </div>
          </div>
        </div>

        {/* Quick-Access Canonical Hour Pill Chips */}
        {hymns.length > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
              <span className="text-[11px] font-bold text-gray-500 whitespace-nowrap mr-1">
                ፈጣን ምርጫ:
              </span>
              {hymns.map((hymn) => {
                const isSelected = hymn.id === selectedId;
                return (
                  <button
                    key={hymn.id}
                    onClick={() => setSelectedId(hymn.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-blue-600 text-white shadow'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span>⏰</span>
                    <span>{hymn.title1}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      {selectedHymn ? (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 space-y-6">
          <div className="border-b border-gray-200 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center text-xl font-bold">
                ⏰
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{selectedHymn.title1}</h2>
                <span className="text-xs text-gray-500">ክፍል #{selectedHymn.id}</span>
              </div>
            </div>
            {canEditContent && (
              <button
                onClick={() => handleOpenEdit(selectedHymn)}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow transition-all cursor-pointer"
              >
                <span>✏️</span>
                <span>ይህን ሰዓታት አርትዕ (Edit in DB)</span>
              </button>
            )}
          </div>

          <div className="space-y-6">
            {selectedHymn.content && selectedHymn.content.map((section, index) => (
              <div key={index} className="border border-blue-100 rounded-2xl overflow-hidden bg-gradient-to-br from-white to-blue-50/20 shadow-sm">
                {section.sub_title && (
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5 font-bold text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px]">
                        {index + 1}
                      </span>
                      <span>{highlightText(section.sub_title)}</span>
                    </span>
                  </div>
                )}
                <div className="p-4 sm:p-6 space-y-4">
                  {section.contains && section.contains.map((c, i) => (
                    <div key={i} className="border-l-4 border-blue-500 pl-4 py-1 space-y-2">
                      {c.title && (
                        <div>
                          <span className="inline-block bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-md text-xs font-bold">
                            {highlightText(c.title)}
                          </span>
                        </div>
                      )}
                      <p className="text-gray-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                        {highlightText(c.main)}
                      </p>

                      <div className="mt-3 pt-2 border-t border-gray-100">
                        <SocialActions
                          contentId={`seatat_${selectedHymn.id}_${index}_${i}`}
                          contentType="seatat"
                          title={`${c.title || ''} - ${selectedHymn.title1}`}
                          content={c.main}
                          showBookmark={true}
                          showShare={true}
                          showLike={true}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl shadow border border-gray-200 text-gray-500">
          <div className="text-4xl mb-2">⏰</div>
          <p className="font-semibold">እባክዎ ከላይ የሰዓታት ክፍል ይምረጡ</p>
        </div>
      )}

      {/* Content Editor Modal */}
      <ContentEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={handleEditorSaved}
        initialData={editingItem}
        isNew={isNewContent}
        contentType="seatat"
      />
    </div>
  );
}
